import {
  ApiNav,
  AudiencesNav,
  BatchNav,
  ClearbitIdentity,
  CompaniesNav,
  DashboardNav,
  FitCompany,
  Global,
  GoExternal,
  HelpNav,
  PeopleNav,
  Prospector,
  RevealBlock,
  SettingsNav,
  UsageAltNav,
  Website,
  ZapAlt,
} from '@clearkit/icons';
import { CKNavigationBar } from 'clearkit';
import { FC, useEffect, useState } from 'react';
import { withRouter } from 'react-router';
import { NavLink, RouteComponentProps } from 'react-router-dom';

import { AccountFlagEnum } from '~/generated/graphql';
import useFeatureFlag from '~/hooks/account/useFeatureFlag';
import { useHasUsageAccess } from '~/hooks/account/usePlatformAccess';
import paths from '~/lib/paths';

import { WhenProspector } from '../policies/WhenProspector';
import { NavBarBottomMenu } from './NavBarBottomMenu';
import { NavRouterItem } from './NavRouterItem';

type NavBarProps = RouteComponentProps & {
  beta?: boolean;
  view?: string;
};

const LOCAL_STORAGE_KEY = 'clearbit-navIsExpanded';

const NavBar: FC<NavBarProps> = ({
  history: {
    location: { pathname, search },
  },
}) => {
  /**
   * Feature flags
   */
  const hasUsageFlag = useFeatureFlag(AccountFlagEnum.XShowUsageOnPlatform)
    .enabled;
  const hasUsageAccess = useHasUsageAccess() && hasUsageFlag;
  const hasAdvertisingAccess = useFeatureFlag(
    AccountFlagEnum.AllowPlatformAdvertisingMenu,
  ).enabled;
  const hasNewSettingsFlag = useFeatureFlag(
    AccountFlagEnum.RolloutPlatformNewSettings,
  ).enabled;

  /**
   * Local storage and state for navigation bar isExpanded
   */
  const localStorageIsExpanded =
    localStorage?.getItem(LOCAL_STORAGE_KEY) !== 'false';
  const [isExpanded, setIsExpanded] = useState<boolean>(
    localStorageIsExpanded || true,
  );

  const handleExpandNavBar = () => {
    setIsExpanded(!isExpanded);
    localStorage.setItem(LOCAL_STORAGE_KEY, String(!isExpanded));
  };

  useEffect(() => {
    setIsExpanded(localStorageIsExpanded);
  }, []);

  const HomeLink = () => (
    <CKNavigationBar.Logo as={NavLink} label="Home" to="/">
      <ClearbitIdentity />
    </CKNavigationBar.Logo>
  );
  HomeLink.displayName = 'CKNavigationBar.Logo';

  /**
   * The new free trial will grant platform access, but we still don't want to
   * show the navigation bar sometimes. For example, when updating their CRM settings in
   * grow.clearbit.com or during onboarding.
   * To make it flexible, pass ?navigation=hide in the URL.
   * */
  if (
    pathname.startsWith('/onboarding') ||
    pathname.startsWith('/get-started') ||
    search.includes('navigation=hide')
  ) {
    return null;
  }

  return (
    <CKNavigationBar isExpanded={isExpanded} onToggle={handleExpandNavBar}>
      <HomeLink />

      <CKNavigationBar.Section>
        <NavRouterItem exact label="Home" to="/">
          <DashboardNav />
        </NavRouterItem>
      </CKNavigationBar.Section>

      <CKNavigationBar.Section>
        <NavRouterItem label="Target Markets" to="/tam-calculator">
          <FitCompany />
        </NavRouterItem>

        <NavRouterItem label="Visitor Reports" to="/visitor-report">
          <Website />
        </NavRouterItem>

        <WhenProspector>
          <NavRouterItem label="Prospector" to="/prospector">
            <Prospector />
          </NavRouterItem>
        </WhenProspector>

        <NavRouterItem label="People" to="/people">
          <PeopleNav />
        </NavRouterItem>

        <NavRouterItem label="Companies" to="/companies">
          <CompaniesNav />
        </NavRouterItem>
      </CKNavigationBar.Section>

      <CKNavigationBar.Section>
        <NavRouterItem label="Integrations" to="/integrations">
          <ZapAlt />
        </NavRouterItem>

        <NavRouterItem label="Batch" to="/batches">
          <BatchNav />
        </NavRouterItem>

        <NavRouterItem exact={false} label="Lookup" to="/lookup">
          <RevealBlock />
        </NavRouterItem>

        {!hasNewSettingsFlag ? (
          <NavRouterItem label="APIs" to="/api">
            <ApiNav />
          </NavRouterItem>
        ) : null}
      </CKNavigationBar.Section>

      <CKNavigationBar.Section>
        {hasAdvertisingAccess ? (
          <NavRouterItem label="Advertising" to={paths.audiences()}>
            <AudiencesNav />
          </NavRouterItem>
        ) : null}
        <CKNavigationBar.Item
          href="https://chrome.google.com/webstore/detail/clearbit-connect-free-ver/pmnhcgfcafcnkbengdcanjablaabjplo"
          label="Connect"
          target="_blank"
        >
          <CKNavigationBar.ItemLeft>
            <Global />
          </CKNavigationBar.ItemLeft>
          <CKNavigationBar.ItemRight>
            <GoExternal />
          </CKNavigationBar.ItemRight>
        </CKNavigationBar.Item>
      </CKNavigationBar.Section>

      <CKNavigationBar.Section>
        {hasUsageAccess && !hasNewSettingsFlag ? (
          <NavRouterItem label="Usage" to="/settings/usage">
            <UsageAltNav />
          </NavRouterItem>
        ) : null}

        {hasNewSettingsFlag ? (
          <NavRouterItem label="Settings" to="/settings">
            <SettingsNav />
          </NavRouterItem>
        ) : null}

        <CKNavigationBar.Item
          as="a"
          href="https://help.clearbit.com/hc/en-us/"
          label="Support"
          rel="noopener noreferrer"
          target="_blank"
        >
          <CKNavigationBar.ItemLeft>
            <HelpNav />
          </CKNavigationBar.ItemLeft>
        </CKNavigationBar.Item>
      </CKNavigationBar.Section>

      <NavBarBottomMenu />
    </CKNavigationBar>
  );
};

export default withRouter(NavBar);
